/**
 * chain-eos/transfer.ts — Token transfer transactions.
 * Uses WharfKit session — Fuel handles CPU/NET automatically.
 */

import { Asset, Name } from '@wharfkit/antelope'
import type { Session } from './session.js'
import { TOKENS, type TokenSymbol } from './config.js'

export interface TransferParams {
  from: string
  to: string
  amount: number
  symbol: TokenSymbol | string
  memo?: string
  /**
   * Token contract account (e.g. `core.vaulta`, `token.pcash`).
   * Required for tokens outside of TOKENS; falls back to the TOKENS entry
   * matching `symbol` when omitted.
   */
  contract?: string
  /** Asset precision (decimals). Falls back to TOKENS entry, then 4. */
  precision?: number
}

export interface TransferResult {
  txid: string
  blockNum?: number
}

/** Resolves contract + precision for any token — known (TOKENS) or discovered on-chain. */
function resolveToken(params: TransferParams): { contract: string; precision: number } {
  const knownToken = Object.values(TOKENS).find(t => t.symbol === params.symbol)

  const contract = String(params.contract ?? knownToken?.contract ?? '').trim()
  if (!contract) {
    throw new Error(`Unknown token contract for symbol: ${params.symbol}`)
  }

  const precision = Number.isFinite(params.precision)
    ? Number(params.precision)
    : (knownToken?.precision ?? 4)

  return { contract, precision }
}

export async function transfer(
  session: Session,
  params: TransferParams,
): Promise<TransferResult> {
  const { from, to, amount, symbol, memo = '' } = params
  const { contract, precision } = resolveToken(params)

  const asset = Asset.fromFloat(amount, `${precision},${symbol}`)

  const result = await session.transact({
    action: {
      account: contract,
      name: 'transfer',
      authorization: [{ actor: from, permission: 'active' }],
      data: {
        from: Name.from(from),
        to: Name.from(to),
        quantity: asset,
        memo,
      },
    },
  })

  const txid = (result.response as { transaction_id?: string })?.transaction_id ?? ''
  const blockNum = (result.response as { processed?: { block_num?: number } })?.processed?.block_num
  return { txid, blockNum }
}

/**
 * Returns null if valid, or a key string for error message if not.
 * Pass `availableBalance` to also validate against the account's balance.
 */
export function validateTransfer(
  params: TransferParams,
  availableBalance?: number,
): string | null {
  if (!params.symbol) return 'noToken'
  if (!params.to || params.to.length < 1) return 'noRecipient'
  if (params.to.length > 13) return 'invalidAccount'
  if (!/^[a-z1-5.]+$/.test(params.to)) return 'invalidAccount'
  if (!Number.isFinite(params.amount) || params.amount <= 0) return 'zeroAmount'
  if (params.from === params.to) return 'selfTransfer'
  if (availableBalance !== undefined && params.amount > availableBalance) {
    return 'insufficientBalance'
  }
  return null
}
